import React, { useState, useEffect, useRef, useCallback } from 'react';
import PocketBase from 'pocketbase';
import './App.css';

const PB_URL = 'https://pb.cameraplugin.com';
const SELFIE_SOUND = 'https://assets.mixkit.co/active_storage/sfx/1931/1931-preview.mp3';
const PAYMENT_SOUND = 'https://assets.mixkit.co/active_storage/sfx/1084/1084-preview.mp3';

function timeAgo(dateStr) {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return mins + 'm ago';
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return hrs + 'h ago';
    return Math.floor(hrs / 24) + 'd ago';
}

function getStatusBadge(status) {
    if (!status) return <span className="badge badge-default">Unknown</span>;
    const s = status.toLowerCase().trim();
    if (['success', 'done', 'completed', 'active', 'approved'].includes(s))
        return <span className="badge badge-success">Success</span>;
    if (['pending', 'waiting', 'started', 'processing'].includes(s))
        return <span className="badge badge-pending">Pending</span>;
    if (['failed', 'error', 'rejected', 'expired'].includes(s))
        return <span className="badge badge-error">Failed</span>;
    return <span className="badge badge-default">{status}</span>;
}

function Toast({ message, type, onDone }) {
    const [visible, setVisible] = useState(true);
    useEffect(() => {
        const t = setTimeout(() => {
            setVisible(false);
            setTimeout(onDone, 300);
        }, 3000);
        return () => clearTimeout(t);
    }, [onDone]);
    return <div className={`toast ${type}`} style={{ opacity: visible ? 1 : 0 }}>{message}</div>;
}

function SelfieCard({ record, isNew, pb }) {
    const [opened, setOpened] = useState(!!record.openedSelfie);
    const [status, setStatus] = useState(record.statusSelfie || '');
    const [saving, setSaving] = useState(false);
    const link = record.linkSelfie || '';
    const truncated = link.length > 60 ? link.substring(0, 60) + '...' : link;
    const needsPulse = !record.openedSelfie && !!link;

    const handleOpen = async () => {
        setOpened(true);
        try {
            await pb.collection('selfie_data').update(record.id, { openedSelfie: true });
        } catch (err) {
            console.error('Mark opened failed:', err);
        }
    };

    const handleStatusChange = async (newStatus) => {
        setStatus(newStatus);
        setSaving(true);
        try {
            await pb.collection('selfie_data').update(record.id, { statusSelfie: newStatus });
        } catch (err) {
            console.error('Status update failed:', err);
        }
        setSaving(false);
    };

    return (
        <div className={`card${isNew ? ' flash-selfie' : ''}${needsPulse ? ' card-pulse-blue' : ''}`}>
            <div className="card-inner">
                <div className="card-left">
                    <div className="card-header">
                        <div className="avatar blue">{(record.username || '?')[0].toUpperCase()}</div>
                        <div>
                            <div className="card-username">{record.username || 'N/A'}</div>
                            <div className="card-time">{timeAgo(record.created)}</div>
                        </div>
                    </div>
                    <div className="card-link-area">
                        {link ? (
                            <>
                                <span className="card-link" title={link}>{truncated}</span>
                                <button className="action-btn copy-btn" onClick={() => { navigator.clipboard.writeText(link); }}>
                                    <CopyIcon /> Copy
                                </button>
                                {!opened ? (
                                    <a href={link} target="_blank" rel="noopener noreferrer" className="action-btn open-btn open-btn-blue" onClick={handleOpen}>
                                        <OpenIcon /> Open
                                    </a>
                                ) : (
                                    <a href={link} target="_blank" rel="noopener noreferrer" className="opened-label">
                                        <CheckIcon /> Opened
                                    </a>
                                )}
                            </>
                        ) : <span className="card-no-link">No link</span>}
                    </div>
                </div>
                <div className="status-area">
                    <select
                        className={`status-select ${status.toLowerCase()}`}
                        value={status}
                        disabled={saving}
                        onChange={e => handleStatusChange(e.target.value)}
                    >
                        <option value="">-- Status --</option>
                        <option value="pending">Pending</option>
                        <option value="started">Started</option>
                        <option value="processing">Processing</option>
                        <option value="success">Success</option>
                        <option value="failed">Failed</option>
                    </select>
                </div>
            </div>
        </div>
    );
}

function PaymentCard({ record, isNew, pb }) {
    const [opened, setOpened] = useState(!!record.openedPayment);
    const [status, setStatus] = useState(record.statusPayment || '');
    const [saving, setSaving] = useState(false);
    const link = record.linkPayment || '';
    const truncated = link.length > 60 ? link.substring(0, 60) + '...' : link;
    const needsPulse = !record.openedPayment && !!link;

    const handleOpen = async () => {
        setOpened(true);
        try {
            await pb.collection('payment_data').update(record.id, { openedPayment: true });
        } catch (err) {
            console.error('Mark opened failed:', err);
        }
    };

    const handleStatusChange = async (newStatus) => {
        setStatus(newStatus);
        setSaving(true);
        try {
            await pb.collection('payment_data').update(record.id, { statusPayment: newStatus });
        } catch (err) {
            console.error('Status update failed:', err);
        }
        setSaving(false);
    };

    return (
        <div className={`card${isNew ? ' flash-payment' : ''}${needsPulse ? ' card-pulse-purple' : ''}`}>
            <div className="card-inner">
                <div className="card-left">
                    <div className="card-header">
                        <div className="avatar purple">{(record.username || '?')[0].toUpperCase()}</div>
                        <div>
                            <div className="card-username">{record.username || 'N/A'}</div>
                            <div className="card-time">{timeAgo(record.created)}</div>
                        </div>
                    </div>
                    <div className="card-link-area">
                        {link ? (
                            <>
                                <span className="card-link" title={link}>{truncated}</span>
                                <button className="action-btn copy-btn" onClick={() => { navigator.clipboard.writeText(link); }}>
                                    <CopyIcon /> Copy
                                </button>
                                {!opened ? (
                                    <a href={link} target="_blank" rel="noopener noreferrer" className="action-btn open-btn open-btn-purple" onClick={handleOpen}>
                                        <OpenIcon /> Open
                                    </a>
                                ) : (
                                    <a href={link} target="_blank" rel="noopener noreferrer" className="opened-label">
                                        <CheckIcon /> Opened
                                    </a>
                                )}
                            </>
                        ) : <span className="card-no-link">No link</span>}
                    </div>
                </div>
                <div className="status-area">
                    <select
                        className={`status-select ${status.toLowerCase()}`}
                        value={status}
                        disabled={saving}
                        onChange={e => handleStatusChange(e.target.value)}
                    >
                        <option value="">-- Status --</option>
                        <option value="pending">Pending</option>
                        <option value="started">Started</option>
                        <option value="processing">Processing</option>
                        <option value="success">Success</option>
                        <option value="failed">Failed</option>
                    </select>
                </div>
            </div>
        </div>
    );
}

function CopyIcon() {
    return <svg fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>;
}

function OpenIcon() {
    return <svg fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/></svg>;
}

function CheckIcon() {
    return <svg fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>;
}

function CameraIcon() {
    return <svg style={{width:18,height:18}} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>;
}

function CardIcon() {
    return <svg style={{width:18,height:18}} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/></svg>;
}

function LoginIcon() {
    return <svg style={{width:20,height:20}} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"/></svg>;
}

function LogoutIcon() {
    return <svg style={{width:18,height:18}} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/></svg>;
}

function VoiceOnIcon() {
    return <svg style={{width:18,height:18}} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.536 8.464a5 5 0 010 7.072M18.364 5.636a9 9 0 010 12.728M11 5L6 9H2v6h4l5 4V5z"/></svg>;
}

function VoiceOffIcon() {
    return <svg style={{width:18,height:18}} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"/><path strokeLinecap="round" strokeLinejoin="round" d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2"/></svg>;
}

function speak(text) {
    try {
        if (!('speechSynthesis' in window)) return;
        window.speechSynthesis.cancel();
        const synth = window.speechSynthesis;
        const trySpeak = () => {
            const utterance = new SpeechSynthesisUtterance(text);
            utterance.lang = 'fr-FR';
            utterance.rate = 1;
            utterance.pitch = 1;
            const voices = synth.getVoices();
            const frVoice = voices.find(v => v.lang.startsWith('fr'));
            if (frVoice) utterance.voice = frVoice;
            synth.speak(utterance);
        };
        const voices = synth.getVoices();
        if (voices.length > 0) {
            trySpeak();
        } else {
            synth.addEventListener('voiceschanged', trySpeak, { once: true });
        }
    } catch {}
}

function LoginPage({ onLogin }) {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const res = await fetch(PB_URL + '/api/dashboard/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password })
            });
            const data = await res.json();

            if (!data.ok) {
                setError(data.error || 'Login failed');
                setLoading(false);
                return;
            }

            localStorage.setItem('dashboard_user', JSON.stringify(data.user));
            onLogin(data.user);
        } catch (err) {
            setError('Connection error');
        }
        setLoading(false);
    };

    return (
        <div className="login-page">
            <div className="login-bg-grid" />
            <div className="login-card">
                <div className="login-logo">
                    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{width:28,height:28}}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2"/>
                    </svg>
                </div>
                <h2>Dashboard Login</h2>
                <p className="login-subtitle">Sign in to access your data</p>
                <form onSubmit={handleSubmit}>
                    <div className="login-field">
                        <label>Username</label>
                        <input
                            type="text"
                            className="login-input"
                            placeholder="Enter your username"
                            value={username}
                            onChange={e => setUsername(e.target.value)}
                            disabled={loading}
                            autoFocus
                        />
                    </div>
                    <div className="login-field">
                        <label>Password</label>
                        <input
                            type="password"
                            className="login-input"
                            placeholder="Enter your password"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            disabled={loading}
                        />
                    </div>
                    {error && <div className="login-error">{error}</div>}
                    <button type="submit" className="login-btn" disabled={loading}>
                        {loading ? 'Signing in...' : <><LoginIcon /> Sign In</>}
                    </button>
                </form>
            </div>
        </div>
    );
}

export default function App() {
    const [authUser, setAuthUser] = useState(null);
    const [authChecked, setAuthChecked] = useState(false);
    const [pbUrl] = useState(PB_URL);
    const [connected, setConnected] = useState(false);
    const [tab, setTab] = useState('selfie');
    const [selfieRecords, setSelfieRecords] = useState([]);
    const [paymentRecords, setPaymentRecords] = useState([]);
    const [newSelfieIds, setNewSelfieIds] = useState(new Set());
    const [newPaymentIds, setNewPaymentIds] = useState(new Set());
    const [selfieAlert, setSelfieAlert] = useState(false);
    const [paymentAlert, setPaymentAlert] = useState(false);
    const [toasts, setToasts] = useState([]);
    const [voiceMode, setVoiceMode] = useState(() => {
        const saved = localStorage.getItem('voice_mode');
        return saved === null ? true : saved === 'true';
    });
    const voiceModeRef = useRef(voiceMode);
    useEffect(() => { voiceModeRef.current = voiceMode; }, [voiceMode]);

    const pbRef = useRef(null);
    const knownSelfieRef = useRef(new Set());
    const knownPaymentRef = useRef(new Set());
    const selfieSoundRef = useRef(null);
    const paymentSoundRef = useRef(null);

    const addToast = useCallback((message, type) => {
        const id = Date.now() + Math.random();
        setToasts(prev => [...prev, { id, message, type }]);
    }, []);

    const removeToast = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    const toggleVoice = useCallback(() => {
        setVoiceMode(prev => {
            const next = !prev;
            localStorage.setItem('voice_mode', String(next));
            return next;
        });
    }, []);

    useEffect(() => {
        const saved = localStorage.getItem('dashboard_user');
        if (saved) {
            try {
                const user = JSON.parse(saved);
                fetch(PB_URL + '/api/dashboard/verify', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ username: user.username, session_token: user.session_token })
                })
                .then(r => r.json())
                .then(data => {
                    if (data.ok) {
                        setAuthUser(data.user);
                    } else {
                        localStorage.removeItem('dashboard_user');
                    }
                    setAuthChecked(true);
                })
                .catch(() => {
                    setAuthUser(user);
                    setAuthChecked(true);
                });
            } catch {
                localStorage.removeItem('dashboard_user');
                setAuthChecked(true);
            }
        } else {
            setAuthChecked(true);
        }
    }, []);

    useEffect(() => {
        selfieSoundRef.current = new Audio(SELFIE_SOUND);
        paymentSoundRef.current = new Audio(PAYMENT_SOUND);
        const unlock = () => {
            try { selfieSoundRef.current.play().then(() => { selfieSoundRef.current.pause(); selfieSoundRef.current.currentTime = 0; }).catch(() => {}); } catch {}
            try { paymentSoundRef.current.play().then(() => { paymentSoundRef.current.pause(); paymentSoundRef.current.currentTime = 0; }).catch(() => {}); } catch {}
            document.removeEventListener('click', unlock);
            document.removeEventListener('keydown', unlock);
        };
        document.addEventListener('click', unlock);
        document.addEventListener('keydown', unlock);
    }, []);

    const handleLogout = useCallback(() => {
        localStorage.removeItem('dashboard_user');
        setAuthUser(null);
        setSelfieRecords([]);
        setPaymentRecords([]);
        setConnected(false);
        try { pbRef.current?.collection('selfie_data')?.unsubscribe('*'); } catch {}
        try { pbRef.current?.collection('payment_data')?.unsubscribe('*'); } catch {}
    }, []);

    const handleLogin = useCallback((user) => {
        setAuthUser(user);
    }, []);

    useEffect(() => {
        if (!pbUrl || !authUser) return;

        const pb = new PocketBase(pbUrl);
        pbRef.current = pb;
        let cancelled = false;

        async function init() {
            try {
                const filterStr = `dashboard_user = "${authUser.id}"`;

                const selfieData = await pb.collection('selfie_data').getFullList({
                    sort: '-created',
                    filter: filterStr
                });
                if (cancelled) return;
                setSelfieRecords(selfieData);
                selfieData.forEach(r => knownSelfieRef.current.add(r.id));

                const paymentData = await pb.collection('payment_data').getFullList({
                    sort: '-created',
                    filter: filterStr
                });
                if (cancelled) return;
                setPaymentRecords(paymentData);
                paymentData.forEach(r => knownPaymentRef.current.add(r.id));

                await pb.collection('selfie_data').subscribe('*', (e) => {
                    if (cancelled) return;
                    if (e.action === 'create' && !knownSelfieRef.current.has(e.record.id)) {
                        if (e.record.dashboard_user !== authUser.id) return;
                        knownSelfieRef.current.add(e.record.id);
                        setSelfieRecords(prev => [e.record, ...prev]);
                        setNewSelfieIds(prev => new Set(prev).add(e.record.id));
                        setSelfieAlert(true);
                        const uname = e.record.username || 'Unknown';
                        if (voiceModeRef.current) {
                            speak('Selfie de ' + uname);
                        } else {
                            try { selfieSoundRef.current.currentTime = 0; selfieSoundRef.current.play().catch(() => {}); } catch {}
                        }
                        addToast('New selfie: ' + uname, 'info');
                    } else if (e.action === 'update') {
                        if (e.record.dashboard_user !== authUser.id) return;
                        setSelfieRecords(prev => prev.map(r => r.id === e.record.id ? e.record : r));
                    } else if (e.action === 'delete') {
                        setSelfieRecords(prev => prev.filter(r => r.id !== e.record.id));
                        knownSelfieRef.current.delete(e.record.id);
                    }
                });

                await pb.collection('payment_data').subscribe('*', (e) => {
                    if (cancelled) return;
                    if (e.action === 'create' && !knownPaymentRef.current.has(e.record.id)) {
                        if (e.record.dashboard_user !== authUser.id) return;
                        knownPaymentRef.current.add(e.record.id);
                        setPaymentRecords(prev => [e.record, ...prev]);
                        setNewPaymentIds(prev => new Set(prev).add(e.record.id));
                        setPaymentAlert(true);
                        const uname = e.record.username || 'Unknown';
                        if (voiceModeRef.current) {
                            speak('Paiement de ' + uname);
                        } else {
                            try { paymentSoundRef.current.currentTime = 0; paymentSoundRef.current.play().catch(() => {}); } catch {}
                        }
                        addToast('New payment: ' + uname, 'info');
                    } else if (e.action === 'update') {
                        if (e.record.dashboard_user !== authUser.id) return;
                        setPaymentRecords(prev => prev.map(r => r.id === e.record.id ? e.record : r));
                    } else if (e.action === 'delete') {
                        setPaymentRecords(prev => prev.filter(r => r.id !== e.record.id));
                        knownPaymentRef.current.delete(e.record.id);
                    }
                });

                setConnected(true);
            } catch (err) {
                console.error('Init error:', err);
                setConnected(false);
            }
        }

        init();

        return () => {
            cancelled = true;
            try { pb.collection('selfie_data').unsubscribe('*'); } catch {}
            try { pb.collection('payment_data').unsubscribe('*'); } catch {}
        };
    }, [pbUrl, authUser, addToast]);

    useEffect(() => {
        if (selfieAlert) {
            const t = setTimeout(() => setSelfieAlert(false), 5000);
            return () => clearTimeout(t);
        }
    }, [selfieAlert]);

    useEffect(() => {
        if (paymentAlert) {
            const t = setTimeout(() => setPaymentAlert(false), 5000);
            return () => clearTimeout(t);
        }
    }, [paymentAlert]);

    function handleTabChange(t) {
        setTab(t);
        if (t === 'selfie') setSelfieAlert(false);
        if (t === 'payment') setPaymentAlert(false);
    }

    const selfiePending = selfieRecords.filter(r => {
        const s = (r.statusSelfie || '').toLowerCase().trim();
        return ['pending', 'waiting', 'started', 'processing', ''].includes(s);
    }).length;

    const selfieSuccess = selfieRecords.filter(r => {
        const s = (r.statusSelfie || '').toLowerCase().trim();
        return ['success', 'done', 'completed', 'active', 'approved'].includes(s);
    }).length;

    const selfieFailed = selfieRecords.filter(r => {
        const s = (r.statusSelfie || '').toLowerCase().trim();
        return ['failed', 'error', 'rejected', 'expired'].includes(s);
    }).length;

    const paymentPending = paymentRecords.filter(r => {
        const s = (r.statusPayment || '').toLowerCase().trim();
        return ['pending', 'waiting', 'started', 'processing', ''].includes(s);
    }).length;

    const paymentSuccess = paymentRecords.filter(r => {
        const s = (r.statusPayment || '').toLowerCase().trim();
        return ['success', 'done', 'completed', 'active', 'approved'].includes(s);
    }).length;

    const paymentFailed = paymentRecords.filter(r => {
        const s = (r.statusPayment || '').toLowerCase().trim();
        return ['failed', 'error', 'rejected', 'expired'].includes(s);
    }).length;

    if (!authChecked) {
        return (
            <div className="login-page">
                <div className="login-card" style={{textAlign:'center'}}>
                    <div className="login-spinner" />
                    <p style={{color:'var(--text-dim)',marginTop:'16px'}}>Checking session...</p>
                </div>
            </div>
        );
    }

    if (!authUser) {
        return <LoginPage onLogin={handleLogin} />;
    }

    return (
        <div className="app-container">
            <header>
                <div className="logo-area">
                    <div className="logo-icon">
                        <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{width:24,height:24}}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2"/>
                        </svg>
                    </div>
                    <div>
                        <h1>Dashboard <span style={{fontSize:'11px',fontWeight:600,background:'linear-gradient(135deg,#3b82f6,#8b5cf6)',WebkitTextFillColor:'#fff',color:'#fff !important',padding:'2px 8px',borderRadius:'6px',verticalAlign:'middle',marginLeft:'8px',letterSpacing:'0.5px'}}>v2.0</span></h1>
                        <div className="header-sub">Real-time Liveness & Payment Monitor</div>
                    </div>
                </div>
                <div className="header-right">
                    <div className="user-badge">
                        <div className="user-badge-avatar">{(authUser.display_name || authUser.username || '?')[0].toUpperCase()}</div>
                        <span className="user-badge-name">{authUser.display_name || authUser.username}</span>
                    </div>
                    <div className="conn-status">
                        <div className="conn-dot" style={{ background: connected ? '#10b981' : '#ef4444' }} />
                        <span style={{ color: connected ? '#34d399' : '#f87171' }}>
                            {connected ? 'Connected' : 'Connecting...'}
                        </span>
                    </div>
                    <button className={`voice-btn${voiceMode ? ' voice-on' : ''}`} onClick={toggleVoice} title={voiceMode ? 'Voice: ON - Click to switch to Sound' : 'Sound: ON - Click to switch to Voice'}>
                        {voiceMode ? <VoiceOnIcon /> : <VoiceOffIcon />}
                    </button>
                    <button className="logout-btn" onClick={handleLogout} title="Logout">
                        <LogoutIcon />
                    </button>
                </div>
            </header>

            <div className="tabs-container">
                <button
                    className={`tab-btn${tab === 'selfie' ? ' active-selfie' : ''}${selfieAlert && tab !== 'selfie' ? ' alert-selfie' : ''}`}
                    onClick={() => handleTabChange('selfie')}
                >
                    <CameraIcon /> Liveness Selfie
                    <span className="count-badge">{selfieRecords.length}</span>
                    {selfieAlert && tab !== 'selfie' && <span className="tab-alert-dot" />}
                </button>
                <button
                    className={`tab-btn${tab === 'payment' ? ' active-payment' : ''}${paymentAlert && tab !== 'payment' ? ' alert-payment' : ''}`}
                    onClick={() => handleTabChange('payment')}
                >
                    <CardIcon /> Payment
                    <span className="count-badge">{paymentRecords.length}</span>
                    {paymentAlert && tab !== 'payment' && <span className="tab-alert-dot" />}
                </button>
            </div>

            <div className="stats-grid" style={{ gridTemplateColumns: tab === 'selfie' ? 'repeat(4, 1fr)' : 'repeat(4, 1fr)' }}>
                {tab === 'selfie' ? (
                    <>
                        <div className="stat-card selfie">
                            <div className="stat-label">Selfie Entries</div>
                            <div className="stat-value blue">{selfieRecords.length}</div>
                        </div>
                        <div className="stat-card selfie">
                            <div className="stat-label">Selfie Pending</div>
                            <div className="stat-value amber">{selfiePending}</div>
                        </div>
                        <div className="stat-card selfie">
                            <div className="stat-label">Selfie Success</div>
                            <div className="stat-value green">{selfieSuccess}</div>
                        </div>
                        <div className="stat-card selfie">
                            <div className="stat-label">Selfie Failed</div>
                            <div className="stat-value red">{selfieFailed}</div>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="stat-card payment">
                            <div className="stat-label">Payment Entries</div>
                            <div className="stat-value purple">{paymentRecords.length}</div>
                        </div>
                        <div className="stat-card payment">
                            <div className="stat-label">Payment Pending</div>
                            <div className="stat-value amber">{paymentPending}</div>
                        </div>
                        <div className="stat-card payment">
                            <div className="stat-label">Payment Success</div>
                            <div className="stat-value green">{paymentSuccess}</div>
                        </div>
                        <div className="stat-card payment">
                            <div className="stat-label">Payment Failed</div>
                            <div className="stat-value red">{paymentFailed}</div>
                        </div>
                    </>
                )}
            </div>

            <div className={`panel${tab !== 'selfie' ? ' hidden' : ''}`}>
                {selfieRecords.length === 0 ? (
                    <div className="empty-state">
                        <svg fill="none" stroke="currentColor" strokeWidth="1" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                        </svg>
                        <p>No selfie data yet for <strong>{authUser.username}</strong>. Waiting for entries...</p>
                    </div>
                ) : selfieRecords.map(r => (
                    <SelfieCard key={r.id} record={r} isNew={newSelfieIds.has(r.id)} pb={pbRef.current} />
                ))}
            </div>

            <div className={`panel${tab !== 'payment' ? ' hidden' : ''}`}>
                {paymentRecords.length === 0 ? (
                    <div className="empty-state">
                        <svg fill="none" stroke="currentColor" strokeWidth="1" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
                        </svg>
                        <p>No payment data yet for <strong>{authUser.username}</strong>. Waiting for entries...</p>
                    </div>
                ) : paymentRecords.map(r => (
                    <PaymentCard key={r.id} record={r} isNew={newPaymentIds.has(r.id)} pb={pbRef.current} />
                ))}
            </div>

            <div className="toast-container">
                {toasts.map(t => <Toast key={t.id} message={t.message} type={t.type} onDone={() => removeToast(t.id)} />)}
            </div>
        </div>
    );
}
